import { Annotation, EditorState, StateEffect } from '@codemirror/state';
import { EditorView, ViewUpdate } from '@codemirror/view';
import { debounce, noop } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';

import { CodeMirrorEditorProps } from 'dashboard/components-lib/CodeEditor/CodeEditor';
import { getDefaultExtensions } from 'dashboard/components-lib/CodeEditor/getDefaultExtensions';

const External = Annotation.define<boolean>();

// Call onChange max 3 times a second
const DEBOUNCE_TIME = 333;

export interface UseCodeMirror extends CodeMirrorEditorProps {
  container?: HTMLDivElement | null;
}

export function useCodeMirror(props: UseCodeMirror) {
  const {
    autoFocus,
    code,
    completionSources,
    disableDebounce = false,
    editable = true,
    extensions = [],
    hasError = false,
    initialState,
    language,
    onChange,
    onCreateEditor,
    onCommandEnter,
    onUpdate,
    placeholder: placeholderStr = '',
    readOnly = false,
    selection,
    root,
    schemas,
  } = props;
  const [container, setContainer] = useState<HTMLDivElement>();
  const [view, setView] = useState<EditorView>();
  const [state, setState] = useState<EditorState>();

  const onChangeDebounced = useMemo(
    () => debounce(disableDebounce ? 0 : DEBOUNCE_TIME, onChange || noop),
    [disableDebounce, onChange],
  );

  const updateListener = EditorView.updateListener.of((vu: ViewUpdate) => {
    if (
      vu.docChanged &&
      typeof onChange === 'function' &&
      // Fix echoing of the remote changes:
      // If transaction is market as remote we don't have to call `onChange` handler again
      !vu.transactions.some((tr) => tr.annotation(External))
    ) {
      const doc = vu.state.doc;
      const value = doc.toString();
      onChangeDebounced(value, vu);
    }
  });

  const defaultExtensions = getDefaultExtensions({
    completionSources,
    editable,
    hasError,
    language,
    placeholder: placeholderStr,
    onCommandEnter,
    readOnly,
    schemas,
  });

  let getExtensions = [updateListener, ...defaultExtensions];

  if (onUpdate && typeof onUpdate === 'function') {
    getExtensions.push(EditorView.updateListener.of(onUpdate));
  }
  getExtensions = extensions.concat(getExtensions);

  useEffect(() => {
    if (container && !state) {
      const config = {
        doc: code,
        selection,
        extensions: getExtensions,
      };
      const stateCurrent = initialState
        ? EditorState.fromJSON(initialState.json, config, initialState.fields)
        : EditorState.create(config);

      setState(stateCurrent);
      if (!view) {
        const viewCurrent = new EditorView({
          state: stateCurrent,
          parent: container,
          root,
        });
        setView(viewCurrent);
        onCreateEditor && onCreateEditor(viewCurrent, stateCurrent);
      }
    }
    return () => {
      if (view) {
        setState(undefined);
        setView(undefined);
      }
    };
  }, [container, state]);

  useEffect(() => setContainer(props.container!), [props.container]);

  useEffect(
    () => () => {
      if (view) {
        view.destroy();
        setView(undefined);
      }
    },
    [view],
  );

  useEffect(() => {
    if (autoFocus && view) {
      view.focus();
    }
  }, [autoFocus, view]);

  useEffect(() => {
    if (view) {
      view.dispatch({ effects: StateEffect.reconfigure.of(getExtensions) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, extensions, hasError, onChangeDebounced, onUpdate, placeholderStr, readOnly]);

  useEffect(() => {
    if (code == null) {
      return;
    }
    const currentValue = view ? view.state.doc.toString() : '';
    if (view && code !== currentValue) {
      view.dispatch({
        changes: { from: 0, to: currentValue.length, insert: code || '' },
        annotations: [External.of(true)],
      });
    }
  }, [code, view]);

  return { state, setState, view, setView, container, setContainer };
}
